import { logger } from './logger';

/**
 * Unified UIBlock structure for database storage
 * Used in both bookmarks and user-conversations tables
 */
export interface DBUIBlock {
	id: string;
	component: Record<string, any> | null;
	analysis: string | null;
	user_prompt: string;
}

/**
 * Transform UIBlock.toJSON() output to the unified DB structure
 * Maps: userQuestion -> user_prompt, generatedComponentMetadata -> component, textResponse -> analysis
 * @param uiblock - UIBlock.toJSON() output
 * @param userPrompt - The user's prompt/question
 * @param uiBlockId - The UIBlock ID (passed separately, not in uiblock object)
 */
export function transformUIBlockForDB(uiblock: any, userPrompt: string, uiBlockId: string): DBUIBlock {
	// Extract component from generatedComponentMetadata, or null if empty
	const component = uiblock?.generatedComponentMetadata && Object.keys(uiblock.generatedComponentMetadata).length > 0
		? uiblock.generatedComponentMetadata
		: null;

	return {
		id: uiBlockId || uiblock?.id || '',
		component: component,
		analysis: uiblock?.textResponse || null,
		user_prompt: userPrompt || uiblock?.userQuestion || '',
	};
}

/**
 * Parameters for saving a conversation
 */
export interface SaveConversationParams {
	userId: string;
	userPrompt: string;
	uiblock: any; // UIBlock toJSON() output
	uiBlockId: string; // ID of the UIBlock
	threadId: string;
	collections?: any;
}

/**
 * Result of attempting to save a conversation
 */
export interface SaveConversationResult {
	success: boolean;
	conversationId?: string;
	message?: string;
	error?: string;
}

/**
 * Saves a user conversation to the backend user_conversations table
 * This enables semantic search and conversation history features
 *
 * @param params - Parameters containing userId, userPrompt, uiblock, threadId, and collections
 * @returns Result indicating success/failure and conversation ID if successful
 */
export async function saveConversation(params: SaveConversationParams): Promise<SaveConversationResult> {
	const { userId, userPrompt, uiblock, uiBlockId, threadId, collections } = params;

	// Validate required parameters
	if (!userId) {
		logger.warn('[CONVERSATION_SAVER] Skipping save: userId not provided');
		return {
			success: false,
			error: 'userId is required'
		};
	}

	if (!userPrompt) {
		logger.warn('[CONVERSATION_SAVER] Skipping save: userPrompt not provided');
		return {
			success: false,
			error: 'userPrompt is required'
		};
	}

	if (!uiblock) {
		logger.warn('[CONVERSATION_SAVER] Skipping save: uiblock not provided');
		return {
			success: false,
			error: 'uiblock is required'
		};
	}

	if (!threadId) {
		logger.warn('[CONVERSATION_SAVER] Skipping save: threadId not provided');
		return {
			success: false,
			error: 'threadId is required'
		};
	}

	if (!uiBlockId) {
		logger.warn('[CONVERSATION_SAVER] Skipping save: uiBlockId not provided');
		return {
			success: false,
			error: 'uiBlockId is required'
		};
	}

	// Check if collections are available
	if (!collections?.['user-conversations']?.['create']) {
		logger.debug('[CONVERSATION_SAVER] Collection "user-conversations.create" not available, skipping save');
		return {
			success: false,
			error: 'user-conversations.create collection not available'
		};
	}

	try {
		logger.info(`[CONVERSATION_SAVER] Saving conversation for userId: ${userId}, uiBlockId: ${uiBlockId}, threadId: ${threadId}`);

		// Transform UIBlock to unified DB structure
		const dbUIBlock = transformUIBlockForDB(uiblock, userPrompt, uiBlockId);
		logger.debug(`[CONVERSATION_SAVER] Transformed UIBlock for DB: ${JSON.stringify(dbUIBlock)}`);

		// Step 1: Save to PostgreSQL user_conversations table
		const saveResult = await collections['user-conversations']['create']({
			userId,
			userPrompt,
			uiblock: dbUIBlock,
			threadId
		});

		if (!saveResult?.success) {
			logger.warn(`[CONVERSATION_SAVER] Failed to save conversation to PostgreSQL: ${saveResult?.message || 'Unknown error'}`);
			return {
				success: false,
				error: saveResult?.message || 'Unknown error from backend'
			};
		}

		logger.info(`[CONVERSATION_SAVER] Successfully saved conversation to PostgreSQL, id: ${saveResult.data?.id}`);

		// Step 2: Create embedding in ChromaDB for semantic search
		if (collections?.['conversation-history']?.['embed']) {
			try {
				logger.info('[CONVERSATION_SAVER] Creating embedding for semantic search...');
				const embedResult = await collections['conversation-history']['embed']({
					uiBlockId,
					userPrompt,
					uiBlock: dbUIBlock, // Use the transformed UIBlock
					userId
				});

				if (embedResult?.success) {
					logger.info('[CONVERSATION_SAVER] Successfully created embedding');
				} else {
					logger.warn('[CONVERSATION_SAVER] Failed to create embedding:', embedResult?.error || 'Unknown error');
					// Don't fail the entire operation if embedding fails
				}
			} catch (embedError) {
				const embedErrorMsg = embedError instanceof Error ? embedError.message : String(embedError);
				logger.warn('[CONVERSATION_SAVER] Error creating embedding:', embedErrorMsg);
				// Don't fail the entire operation if embedding fails
			}
		} else {
			logger.debug('[CONVERSATION_SAVER] Embedding collection not available, skipping ChromaDB storage');
		}

		return {
			success: true,
			conversationId: saveResult.data?.id,
			message: 'Conversation saved successfully'
		};
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : String(error);
		logger.error('[CONVERSATION_SAVER] Error saving conversation:', errorMessage);
		return {
			success: false,
			error: errorMessage
		};
	}
}
